import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CalculatorTTMData } from '../../../shared/models/calculator-ttm-data';
import { BaseCalculatorComponent } from '../base-calculator.component';
import { markFormGroupTouched } from '../utils/form-validation.utils';

interface SelectedStock {
  ticker: string;
  name: string;
}

@Component({
    standalone: true,
    selector: 'app-graham-price-calculator',
    imports: [
    CommonModule,
    ReactiveFormsModule
],
    templateUrl: './graham-price-calculator.component.html',
    styleUrl: './graham-price-calculator.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrahamPriceCalculatorComponent extends BaseCalculatorComponent implements OnInit {

  calculatorForm: FormGroup;
  selectedStock: SelectedStock | null = null;
  currentPrice: number | null = null;
  grahamPrice: number | null = null;
  ogGrahamPrice: number | null = null;
  errorMessage: string = '';
  isCalculating: boolean = false;

  constructor(
    private fb: FormBuilder,
  ) {
    super();
    this.calculatorForm = this.fb.group({
      eps: [null, [Validators.required, Validators.min(0.01)]],
      bookValue: [null, [Validators.required, Validators.min(0.01)]],
      medianPE: [15, [Validators.required, Validators.min(0.1)]] // Default to 15
    });
  }

  ngOnInit(): void {

  }

  /**
   * Populate form fields with TTM data from the calculator data service
   */
  protected override populateFormWithTtmData(ttmData: CalculatorTTMData): void {
    const updates: Partial<{ eps: number; bookValue: number; medianPE: number }> = {};

    // Pre-populate EPS (epsDiluted from backend)
    if (ttmData.epsDiluted !== null && ttmData.epsDiluted !== undefined) {
      updates.eps = ttmData.epsDiluted;
    }

    // Pre-populate book value per share (bookValuePerShare from backend)
    if (ttmData.bookValuePerShare !== null && ttmData.bookValuePerShare !== undefined) {
      updates.bookValue = ttmData.bookValuePerShare;
    }

    // Optionally pre-populate median PE if available (priceToEarningsRatio from backend)
    if (ttmData.priceToEarningsRatio !== null && ttmData.priceToEarningsRatio !== undefined) {
      updates.medianPE = ttmData.priceToEarningsRatio;
    }

    // Update the form with the new values
    if (Object.keys(updates).length > 0) {
      this.calculatorForm.patchValue(updates);
      this.logger.debug('Graham Price Calculator pre-populated with TTM data:', updates);
    }
  }

  clearStock(): void {
    this.selectedStock = null;
    this.currentPrice = null;
    this.calculatorForm.reset({
      eps: null,
      bookValue: null,
      medianPE: 15
    });
    this.grahamPrice = null;
    this.ogGrahamPrice = 0;
    this.errorMessage = '';
  }

  calculateGrahamPrice(): void {
    if (this.calculatorForm.invalid) {
      markFormGroupTouched(this.calculatorForm);
      return;
    }

    this.isCalculating = true;
    this.errorMessage = '';
    this.grahamPrice = null;
    this.ogGrahamPrice = null;

    try {
      const eps = this.calculatorForm.get('eps')?.value;
      const bookValue = this.calculatorForm.get('bookValue')?.value;
      const medianPE = this.calculatorForm.get('medianPE')?.value;
      const pbRatio = 1.5; // Fixed as per Graham's formula
      const marketPe = 15;

      // Validate all values are positive
      if (eps <= 0 || bookValue <= 0 || medianPE <= 0) {
        this.errorMessage = 'All values must be positive numbers.';
        this.isCalculating = false;
        return;
      }

      // Calculate Graham Remix Price: √(Median PE × P/B × EPS × Book Value)
      const product = medianPE * pbRatio * eps * bookValue;

      // Calculate OG Graham Price : √(Median PE × P/B × EPS × Book Value)
      const ogProduct = marketPe * pbRatio * eps * bookValue;

      if (product < 0) {
        this.errorMessage = 'Cannot calculate square root of negative number. Please check your inputs.';
        this.isCalculating = false;
        return;
      }

      this.grahamPrice = Math.sqrt(product);
      this.ogGrahamPrice = Math.sqrt(ogProduct);

      // Small delay to show loading state
      setTimeout(() => {
        this.isCalculating = false;
      }, 500);

    } catch (error) {
      this.logger.error('Error calculating Graham Price:', error);
      this.errorMessage = 'An error occurred during calculation. Please try again.';
      this.isCalculating = false;
    }
  }

  // Check if PE ratio is unusually high
  get isPEHigh(): boolean {
    const medianPE = this.calculatorForm.get('medianPE')?.value;
    return medianPE && medianPE > 25;
  }

  get peWarningMessage(): string {
    return 'PE is higher than normal, ensure due diligence as this might not stay this high!';
  }
}
